import { combine, createEffect, createEvent, createStore, sample } from "effector";
import professionMap from "@data/aiToolsProfession/profession-map.json";
import { getRoadmapsConfigFx } from "@features/roadmaps/model";
import { $appLanguage } from "@features/app/model";
import { routerReplace } from "@features/shared/router";
import type { IRoadmapsConfig } from "@features/shared/entities/roadmaps";
import { localizeCardSet, normalizeAppLanguage, type LocalizedCardSet } from "./localize-card-set";

// Profession task cards shown on /ai-tools instead of the generic tools (flag
// ai_tools_profession_set_enabled). Card texts live in data/aiToolsProfession (English
// plus one directory per UI language), the hidden per-card system prompt lives next to
// the /ai/chat route and is resolved there from the card id only.
export type ToolCard = {
    id: string;
    title: string;
    /** Popular cards only: the title split into two fixed lines. */
    titleLines?: string[];
    /** Material Symbols Outlined ligature. */
    icon: string;
    /** One line under the chat title: what to paste. */
    guide: string;
    /** Composer placeholder instead of "Ask anything". */
    placeholder: string;
    /** Text the "Use sample" chip inserts into the composer. */
    sample: string;
    mode: "text" | "image" | "video";
    /** Model name as in the model picker ("Claude Sonnet 5", "ChatGPT Image", "Nano Banana", "Wan"). */
    model: string;
};

export type CardSet = {
    id: string;
    kind: "own" | "general";
    name: string;
    /** First Quick start tab label, e.g. "For dentists"; "For you" in the general set. */
    lead: string;
    icon: string;
    popular: ToolCard[];
    quickStart: ToolCard[];
};

export type CardSetResolution = {
    setId: string;
    /** Funnel token without the "claude" prefix, locale suffix and arm number; analytics only. */
    professionToken: string | null;
};

const localeSuffix = new RegExp(`_(${professionMap.localeSuffixes.join("|")})$`);

/** `clauderealestateagents_es` → `realestateagents`; `accountants_2` → `accountants`. */
export const normalizeVariant = (variant: string | null | undefined): string | null => {
    const token = (variant ?? "")
        .trim()
        .toLowerCase()
        .replace(localeSuffix, "")
        .replace(/_\d+$/, "")
        .replace(localeSuffix, "")
        .replace(/^claude/, "");
    return token || null;
};

/** Same as the backend's roadmaps.Slugify: "AI for Engineers" → "ai-for-engineers". */
export const slugifyRoadmapName = (name: string): string =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/** `ai-for-hospitality-managers` → `hospitalitymanagers`; `claude-for-excel` → `excel`. */
export const tokenFromRoadmapSlug = (slug: string): string => slug.replace(/^(ai|claude)-for-/, "").replace(/[^a-z0-9]/g, "");

const byVariantToken: Record<string, string> = professionMap.byVariantToken;
// The map is keyed by roadmap name (it predates the roadmap sequence, when the
// per-funnel roadmap SET carried its recommended roadmap's name); matched by slug.
const byRoadmapSlug: Record<string, string> = Object.fromEntries(
    Object.entries(professionMap.byRoadmapSetName as Record<string, string>).map(([name, setId]) => [slugifyRoadmapName(name), setId])
);

// Own set by the funnel token, else by the roadmap the funnel recommends (its
// slug, then the token derived from it — new roadmaps appear weekly), else the
// single default set.
export const resolveCardSet = (config: IRoadmapsConfig | null): CardSetResolution => {
    const professionToken = normalizeVariant(config?.variant);
    const slug = config?.recommendation?.slug;
    const setId =
        (professionToken && byVariantToken[professionToken]) ||
        (slug && (byRoadmapSlug[slug] ?? byVariantToken[tokenFromRoadmapSlug(slug)])) ||
        "general";
    return { setId, professionToken };
};

const sameResolution = (a: CardSetResolution | null, b: CardSetResolution | null) =>
    a?.setId === b?.setId && a?.professionToken === b?.professionToken;

export const $cardSetResolution = createStore<CardSetResolution | null>(null, {
    // Re-resolving with the same outcome must not reload the card set.
    updateFilter: (next, prev) => !sameResolution(next, prev)
});
export const $localizedCardSet = createStore<LocalizedCardSet | null>(null);

// One ~5 KB chunk per set (plus one per UI language) instead of shipping all sets to every client.
// A missing or failed locale file means the English set — not an error.
const loadCardSetFx = createEffect(
    async ({ setId, language }: { setId: string; language: string }): Promise<LocalizedCardSet> => {
        const [en, translated] = await Promise.all([
            import(`@data/aiToolsProfession/card-sets/${setId}.json`).then((m) => m.default as CardSet),
            language === "en"
                ? null
                : import(`@data/aiToolsProfession/locales/${language}/card-sets/${setId}.json`)
                      .then((m) => m.default as CardSet)
                      .catch(() => null)
        ]);
        return localizeCardSet(en, translated, language);
    }
);

sample({
    clock: getRoadmapsConfigFx.finally,
    fn: (result) => resolveCardSet(result.status === "done" ? result.result : null),
    target: $cardSetResolution
});

// Reloads when the set is resolved and whenever the UI language changes.
sample({
    clock: [$cardSetResolution.updates, $appLanguage.updates],
    source: { resolution: $cardSetResolution, language: $appLanguage },
    filter: ({ resolution }) => resolution !== null,
    fn: ({ resolution, language }) => ({ setId: resolution!.setId, language: normalizeAppLanguage(language) }),
    target: loadCardSetFx
});

sample({ clock: loadCardSetFx.doneData, target: $localizedCardSet });

// The card whose chat is open in /assistant: set from the ?card= query param,
// cleared on a plain /assistant visit, on "New chat" and on every thread switch.
export const activeCardIdChanged = createEvent<string | null>();
export const $activeCardId = createStore<string | null>(null).on(activeCardIdChanged, (_, id) => id);

// A card chat keeps card + model in the URL for reloads; leaving it drops both,
// so a reload does not reopen the card.
sample({
    clock: activeCardIdChanged,
    filter: (id) => id === null && new URLSearchParams(window.location.search).has("card"),
    fn: () => {
        const params = new URLSearchParams(window.location.search);
        params.delete("card");
        params.delete("model");
        return `${window.location.pathname}?${params.toString()}`;
    },
    target: routerReplace
});

export const findCard = (localized: LocalizedCardSet | null, id: string | null): ToolCard | null =>
    id && localized ? ([...localized.set.popular, ...localized.set.quickStart].find((card) => card.id === id) ?? null) : null;

export const $cardAnalyticsProps = combine($activeCardId, $cardSetResolution, $localizedCardSet, (card_id, resolution, localized) => ({
    card_id,
    tools_set: resolution?.setId ?? null,
    ui_language: localized?.language ?? null
}));
